import type { AgentIdentity, WdaLiveCall, WdaParty } from './clients';

// Background instance: polls the agent's ongoing calls every 3 s and publishes them to
// localStorage, which every mode of the app shares (one storage partition, captured). A call that
// drops off the list has ended (~2 s after hangup); it moves to the recent list (Phase 3 wrap-up).

export interface LiveCall {
  callId: string;
  flowIndex: number;
  remotePhone: string;
  remoteName?: string;
  direction?: string;
  status?: string;
  startedAt?: number;
  /** The agent's flow reached TALKING at some point (a missed call never does). */
  talked: boolean;
}

export interface EndedCall extends LiveCall {
  endedAt: number;
}

export interface CallSnapshot {
  /** When the watcher last succeeded (epoch ms). */
  at: number;
  calls: LiveCall[];
}

export const LIVE_CALLS_KEY = 'halo.liveCalls';
export const RECENT_CALLS_KEY = 'halo.recentCalls';
export const POLL_MS = 3000;
/** Older snapshots mean the watcher isn't running: show no live call rather than a stale one. */
export const STALE_MS = 15_000;
const RECENT_MAX = 20;
const RECENT_TTL_MS = 4 * 3_600_000;

/** The extension is the precise key (it's what the query filters on); email only when there's none. */
export const isMe = (p: WdaParty | undefined, me: AgentIdentity): boolean => {
  if (p?.type !== 'LOCAL') return false;
  if (me.extension) return p.userExtension === me.extension;
  return !!me.email && p.email?.toLowerCase() === me.email;
};

/**
 * The calls where the agent is a LOCAL party. A privileged token can see colleagues' calls
 * (captured), so this filter applies even though the query is already filtered by extension
 * (Invariant 9). Voicemail flows are never the agent talking.
 */
export function agentCalls(raw: WdaLiveCall[], me: AgentIdentity): Omit<LiveCall, 'talked'>[] {
  const out: Omit<LiveCall, 'talked'>[] = [];
  for (const call of raw) {
    const flow = (call.flows ?? []).find((f) => !f.flags?.includes('voicemail') && (isMe(f.caller, me) || isMe(f.callee, me)));
    if (!flow) continue;
    const remote = flow.caller?.type === 'REMOTE' ? flow.caller : flow.callee?.type === 'REMOTE' ? flow.callee : undefined;
    out.push({
      callId: call.id,
      flowIndex: flow.flowIndex,
      remotePhone: flow.remotePhone ?? remote?.phone ?? '',
      remoteName: remote?.name,
      direction: flow.direction,
      status: flow.status,
      startedAt: flow.startTime,
    });
  }
  return out;
}

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function readJson<T>(store: KeyValueStore, key: string, fallback: T): T {
  try {
    const raw = store.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function readLiveCalls(store: KeyValueStore, now = Date.now()): LiveCall[] {
  const snap = readJson<CallSnapshot | null>(store, LIVE_CALLS_KEY, null);
  return snap && now - snap.at < STALE_MS ? snap.calls : [];
}

export function readRecentCalls(store: KeyValueStore): EndedCall[] {
  return readJson<EndedCall[]>(store, RECENT_CALLS_KEY, []);
}

interface WatcherOptions {
  fetchOngoing: () => Promise<{ me: AgentIdentity; calls: WdaLiveCall[] }>;
  store: KeyValueStore;
  now?: () => number;
  intervalMs?: number;
  onError?: (e: unknown) => void;
  /** Calls that just dropped off the live list (hung up). */
  onEnded?: (calls: EndedCall[]) => void;
}

export class CallWatcher {
  private timer: ReturnType<typeof setInterval> | undefined;
  private previous = new Map<string, LiveCall>();
  private busy = false;

  constructor(private readonly opts: WatcherOptions) {}

  get running(): boolean {
    return this.timer !== undefined;
  }

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => void this.tick(), this.opts.intervalMs ?? POLL_MS);
    void this.tick();
  }

  stop(): void {
    clearInterval(this.timer);
    this.timer = undefined;
  }

  async tick(): Promise<void> {
    if (this.busy) return; // a slow request must not stack up polls
    this.busy = true;
    const now = (this.opts.now ?? Date.now)();
    try {
      const { me, calls } = await this.opts.fetchOngoing();
      const current = new Map<string, LiveCall>();
      for (const c of agentCalls(calls, me)) {
        const talked = c.status === 'TALKING' || (this.previous.get(c.callId)?.talked ?? false);
        current.set(c.callId, { ...c, talked });
      }
      const ended = [...this.previous.values()].filter((c) => !current.has(c.callId)).map((c): EndedCall => ({ ...c, endedAt: now }));
      if (ended.length) {
        const recent = [...ended, ...readRecentCalls(this.opts.store).filter((r) => !ended.some((e) => e.callId === r.callId))]
          .filter((r) => now - r.endedAt < RECENT_TTL_MS)
          .slice(0, RECENT_MAX);
        this.opts.store.setItem(RECENT_CALLS_KEY, JSON.stringify(recent));
        this.opts.onEnded?.(ended);
      }
      this.previous = current;
      const snapshot: CallSnapshot = { at: now, calls: [...current.values()] };
      this.opts.store.setItem(LIVE_CALLS_KEY, JSON.stringify(snapshot));
    } catch (e) {
      // Keep the last snapshot; it goes stale on its own if this keeps failing.
      this.opts.onError?.(e);
    } finally {
      this.busy = false;
    }
  }
}
